
import {shouldIgnoreGlobalShortcut} from "../utils/keyboardGuard.js";

import {t, T, localized} from "../digitization/i18n/index.js";
// Four compact visibility-toggle icons next to the burger/menu icon in
// `#potree_quick_buttons` (task DEL J/K/M), plus their "1"-"4" keyboard
// shortcuts - both are just UI shortcuts to the SAME, already-existing LAS
// classification-visibility state every point cloud already has
// (`viewer.classifications`, `viewer.setClassificationVisibility()`) - no
// parallel visibility store (task: "Ingen parallel visibility state", "De
// underliggende classification-navne/data må ikke duplikeres eller
// omdøbes"). The existing, full Filters/Classification panel in the left
// sidebar (`initClassificationList()` in sidebar.js) keeps working exactly
// as before and reads/writes the exact same state - both UIs stay in sync
// automatically via the viewer's own "classification_visibility_changed"
// event, the same event the Filters panel's own checkboxes already listen
// for.
//
// Domain-specific label mapping (task's own, not a general LAS convention -
// deliberately NOT renaming or duplicating the underlying classification
// codes, just giving four of them a quick, task-specific UI label):
//   Støj       -> 7 (low point / noise)
//   Terræn     -> 2 (ground)
//   Anlægsgrav -> 6 (building)
//   Ledninger  -> 9 (water)
export const QUICK_CLASSIFICATIONS = localized([
	{code: 7, key: "1", label: T("Støj"), icon: "classify_noise.svg"},
	{code: 2, key: "2", label: T("Terræn"), icon: "classify_ground.svg"},
	{code: 6, key: "3", label: T("Anlægsgrav"), icon: "classify_building.svg"},
	{code: 9, key: "4", label: T("Ledninger"), icon: "classify_water.svg"},
]);

// Pure (no DOM): "is this classification code currently visible", reading
// straight from the viewer's own `classifications` map (the same object
// `setClassificationVisibility()` mutates and the Filters panel reads) -
// defaults to visible if the scheme has no entry for the code at all, so a
// point cloud whose material hasn't finished initialising never shows a
// toggle as (incorrectly) off. Exported for Level 1 testing - see
// ClassificationQuickToggles.test.js.
export function isClassificationVisible(classifications, code){
	const entry = classifications && classifications[code];
	return !entry || entry.visible !== false;
}

// Pure (no DOM): resolves a keydown event's `key` to the QUICK_CLASSIFICATIONS
// entry it should toggle, or null if the key isn't one of "1".."4". Modifier
// keys (Ctrl/Alt/Meta) and the editable-target guard are the CALLER's
// responsibility (see installClassificationQuickToggles()'s onKeyDown) -
// this function only does the key -> entry lookup, so that lookup itself is
// Level 1 testable independent of any DOM/event object shape.
export function resolveShortcutEntry(key){
	return QUICK_CLASSIFICATIONS.find((q) => q.key === key) || null;
}

// Installs the toggles into `container` (expected to be `#potree_quick_buttons`,
// right next to the burger/menu icon - see viewer.js's loadGUI()) and wires
// the global "1"-"4" keyboard shortcuts. Returns a `{destroy}` handle
// (removes the keydown listener and the DOM it created) - not currently
// called anywhere (the quick buttons live for the viewer's whole lifetime,
// same as the burger/map icons), but included for symmetry/testability
// rather than leaking a global listener with no way to undo it.
export function installClassificationQuickToggles(viewer, container){
	const wrap = document.createElement("div");
	wrap.className = "classification-quick-toggles";
	wrap.style.cssText = "display:flex; flex-direction:row; gap:3px; margin-left:6px;";

	const buttons = new Map(); // code -> button element

	function isVisible(code){
		return isClassificationVisible(viewer.classifications, code);
	}

	function refreshButton(code){
		const btn = buttons.get(code);
		if(!btn) return;
		const on = isVisible(code);
		btn.classList.toggle("active", on);
		btn.style.opacity = on ? "1" : "0.35";
		btn.style.background = on ? "rgba(47,143,214,0.55)" : "transparent";
	}

	function refreshAll(){
		for(const {code} of QUICK_CLASSIFICATIONS) refreshButton(code);
	}

	function toggle(code){
		viewer.setClassificationVisibility(code, !isVisible(code));
		// setClassificationVisibility() dispatches "classification_visibility_changed"
		// itself, which refreshAll() below is already listening for - no need
		// to refresh here too (avoids a double-render on the same change).
	}

	for(const entry of QUICK_CLASSIFICATIONS){
		const btn = document.createElement("img");
		btn.src = new URL(`${Potree.resourcePath}/icons/${entry.icon}`, window.location.href).href;
		btn.title = t("{0} – vis/skjul ({1})", entry.label, entry.key);
		btn.style.cssText = "width:1.6em; height:1.6em; padding:3px; border-radius:4px; cursor:pointer; box-sizing:content-box;";
		btn.onclick = () => toggle(entry.code);
		buttons.set(entry.code, btn);
		wrap.appendChild(btn);
	}

	container.appendChild(wrap);
	refreshAll();

	const onVisibilityChanged = () => refreshAll();
	viewer.addEventListener("classification_visibility_changed", onVisibilityChanged);
	viewer.addEventListener("classifications_changed", onVisibilityChanged);

	// "1".."4" -> toggle the matching classification. Same guard as
	// Digitalisering's own "L"/"Shift+L" (task DEL L: "genbrug/
	// generalisér") - never fires while the user is typing in a geometry
	// popup's Radius/Diameter/Længde/Bredde/Pilhøjde/Kote/Fald field or any
	// other editable control.
	const onKeyDown = (e) => {
		if(e.ctrlKey || e.altKey || e.metaKey) return;
		const entry = resolveShortcutEntry(e.key);
		if(!entry) return;
		if(shouldIgnoreGlobalShortcut()) return;
		toggle(entry.code);
	};
	window.addEventListener("keydown", onKeyDown);

	return {
		destroy(){
			window.removeEventListener("keydown", onKeyDown);
			viewer.removeEventListener("classification_visibility_changed", onVisibilityChanged);
			viewer.removeEventListener("classifications_changed", onVisibilityChanged);
			wrap.remove();
		},
	};
}
